import { api } from "@breakout/backend/convex/_generated/api"
import {
	batchExecute,
	emergencyPause,
	emergencyUnpause,
	encodeEmergencyPause,
	encodeEmergencyUnpause,
	getExplorerTxUrl,
	getMarket,
	type SafeTransaction,
	serializeSafeBatch,
	toSafeTransactionBatch,
	truncateAddress,
} from "@breakout/sdk"
import { toast } from "@breakout/ui/components/Toast"
import { cn } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import { TableSkeleton } from "../components/skeletons/TableRowSkeleton"
import {
	type TransactionStatus,
	TransactionStatusDialog,
} from "../components/TransactionStatusDialog"
import { useExecutionMode } from "../hooks/useExecutionMode"
import { useRegistryConfig } from "../hooks/useRegistryConfig"
import { useAdminConfig } from "../lib/providers"

export const Route = createFileRoute("/emergency")({
	component: EmergencyPage,
})

/** Known market status types from SDK's MarketStatus discriminated union */
type MarketStatusType =
	| "pending"
	| "open"
	| "closed"
	| "resolved"
	| "finalized"
	| "paused"
	| "unknown"

interface MarketWithPauseStatus {
	marketAddress: string
	displayName: string
	statusType: MarketStatusType
	isPaused: boolean
}

function EmergencyPage() {
	const { isConnected, address: connectedAddress } = useAccount()
	const { data: walletClient } = useWalletClient()
	const { publicClient, chainId } = useAdminConfig()
	const { isHotWalletMode, isSafeMode } = useExecutionMode()
	const { resolver: onChainResolver, isLoading: resolverLoading } = useRegistryConfig()
	const queryClient = useQueryClient()

	// Check if connected wallet is the resolver
	const isResolver =
		connectedAddress && onChainResolver
			? getAddress(connectedAddress) === getAddress(onChainResolver)
			: false

	const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null)
	const [selectedMarkets, setSelectedMarkets] = useState<Set<string>>(new Set())
	const [executingMarkets, setExecutingMarkets] = useState<Set<string>>(new Set())
	const [txResults, setTxResults] = useState<
		Map<string, { hash: string; status: "success" | "error" }>
	>(new Map())
	const [isPausingAll, setIsPausingAll] = useState(false)
	const [isUnpausingAll, setIsUnpausingAll] = useState(false)

	// Transaction dialog state
	const [txDialog, setTxDialog] = useState<{
		open: boolean
		status: TransactionStatus
		hash?: string
		title: string
		description: string
		errorMessage: string
		operationCount: number
	}>({
		open: false,
		status: "idle",
		hash: undefined,
		title: "",
		description: "",
		errorMessage: "",
		operationCount: 1,
	})

	// Fetch weeks from Convex (typed import)
	const { data: weeks } = useQuery({
		...convexQuery(api.domains.admin.queries.listWeeks, {}),
		enabled: isConnected,
	})

	// Fetch markets for selected week (typed import)
	const { data: markets } = useQuery({
		...convexQuery(api.domains.markets.queries.list, { weekId: selectedWeekId ?? "" }),
		enabled: !!selectedWeekId && isConnected,
	})

	// Fetch on-chain status for all markets
	const { data: marketStatuses, isLoading: statusLoading } = useQuery({
		queryKey: ["admin", "emergency", selectedWeekId, markets?.map(m => m.marketAddress)],
		queryFn: async (): Promise<MarketWithPauseStatus[]> => {
			if (!markets) {
				return []
			}
			return Promise.all(
				markets.map(async (market): Promise<MarketWithPauseStatus> => {
					const displayName = market.displayName || market.handle || market.marketAddress
					try {
						const onChain = await getMarket(publicClient, {
							address: getAddress(market.marketAddress),
						})
						return {
							marketAddress: market.marketAddress,
							displayName,
							statusType: onChain.status.type,
							isPaused: onChain.status.type === "paused",
						}
					} catch {
						return {
							marketAddress: market.marketAddress,
							displayName,
							statusType: "unknown",
							isPaused: false,
						}
					}
				}),
			)
		},
		enabled: !!markets && markets.length > 0,
		// RPC-based query for on-chain status - needs polling (not indexed)
		// 30s is sufficient for emergency monitoring
		refetchInterval: 30_000,
	})

	// Emergency pause mutation
	const pauseMutation = useMutation({
		mutationFn: async ({ market }: { market: Address }) => {
			if (!walletClient) {
				throw new Error("Wallet not connected")
			}
			return emergencyPause(walletClient, { market })
		},
	})

	// Emergency unpause mutation
	const unpauseMutation = useMutation({
		mutationFn: async ({ market }: { market: Address }) => {
			if (!walletClient) {
				throw new Error("Wallet not connected")
			}
			return emergencyUnpause(walletClient, { market })
		},
	})

	// Handle pause
	const handlePause = async (marketAddress: string) => {
		// Pre-flight: Check wallet is authorized resolver
		if (!onChainResolver) {
			toast.error("Could not fetch resolver from registry. Please try again.")
			return
		}
		if (!connectedAddress || getAddress(connectedAddress) !== getAddress(onChainResolver)) {
			toast.error(
				`Only the resolver can pause markets. Connected: ${truncateAddress(connectedAddress ?? "0x0", 6)}, Resolver: ${truncateAddress(onChainResolver, 6)}`,
			)
			return
		}

		setExecutingMarkets(prev => new Set(prev).add(marketAddress))
		try {
			const hash = await pauseMutation.mutateAsync({ market: getAddress(marketAddress) })

			// Wait for confirmation before marking as success
			const receipt = await publicClient.waitForTransactionReceipt({
				hash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxResults(prev =>
					new Map(prev).set(`pause-${marketAddress}`, { hash, status: "success" }),
				)
				queryClient.invalidateQueries({ queryKey: ["admin", "emergency"] })
			} else {
				console.error("Pause transaction reverted:", hash)
				setTxResults(prev =>
					new Map(prev).set(`pause-${marketAddress}`, { hash: "", status: "error" }),
				)
				toast.error("Pause transaction reverted on chain")
			}
		} catch (error) {
			console.error("Pause failed:", error)
			setTxResults(prev =>
				new Map(prev).set(`pause-${marketAddress}`, { hash: "", status: "error" }),
			)
			toast.error(error instanceof Error ? error.message : "Pause failed")
		} finally {
			setExecutingMarkets(prev => {
				const next = new Set(prev)
				next.delete(marketAddress)
				return next
			})
		}
	}

	// Handle unpause
	const handleUnpause = async (marketAddress: string) => {
		// Pre-flight: Check wallet is authorized resolver
		if (!onChainResolver) {
			toast.error("Could not fetch resolver from registry. Please try again.")
			return
		}
		if (!connectedAddress || getAddress(connectedAddress) !== getAddress(onChainResolver)) {
			toast.error(
				`Only the resolver can unpause markets. Connected: ${truncateAddress(connectedAddress ?? "0x0", 6)}, Resolver: ${truncateAddress(onChainResolver, 6)}`,
			)
			return
		}

		setExecutingMarkets(prev => new Set(prev).add(marketAddress))
		try {
			const hash = await unpauseMutation.mutateAsync({ market: getAddress(marketAddress) })

			// Wait for confirmation before marking as success
			const receipt = await publicClient.waitForTransactionReceipt({
				hash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxResults(prev =>
					new Map(prev).set(`unpause-${marketAddress}`, { hash, status: "success" }),
				)
				queryClient.invalidateQueries({ queryKey: ["admin", "emergency"] })
			} else {
				console.error("Unpause transaction reverted:", hash)
				setTxResults(prev =>
					new Map(prev).set(`unpause-${marketAddress}`, { hash: "", status: "error" }),
				)
				toast.error("Unpause transaction reverted on chain")
			}
		} catch (error) {
			console.error("Unpause failed:", error)
			setTxResults(prev =>
				new Map(prev).set(`unpause-${marketAddress}`, { hash: "", status: "error" }),
			)
			toast.error(error instanceof Error ? error.message : "Unpause failed")
		} finally {
			setExecutingMarkets(prev => {
				const next = new Set(prev)
				next.delete(marketAddress)
				return next
			})
		}
	}

	// Handle pause all active markets in a single batch transaction
	const handlePauseAll = async () => {
		if (!walletClient || !marketStatuses) {
			return
		}

		// Pre-flight: Check wallet is authorized resolver
		if (!onChainResolver) {
			toast.error("Could not fetch resolver from registry. Please try again.")
			return
		}
		if (!connectedAddress || getAddress(connectedAddress) !== getAddress(onChainResolver)) {
			toast.error(
				`Only the resolver can pause markets. Connected: ${truncateAddress(connectedAddress ?? "0x0", 6)}, Resolver: ${truncateAddress(onChainResolver, 6)}`,
			)
			return
		}

		// Filter active markets that haven't been paused yet
		const marketsToPause = marketStatuses.filter(
			m => !m.isPaused && m.statusType !== "unknown" && !txResults.get(`pause-${m.marketAddress}`),
		)

		if (marketsToPause.length === 0) {
			return
		}

		setIsPausingAll(true)

		// Open dialog in pending state
		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Pause All Markets",
			description: `Pausing ${marketsToPause.length} markets...`,
			errorMessage: "",
			operationCount: marketsToPause.length,
		})

		// Mark all markets as executing
		setExecutingMarkets(prev => {
			const next = new Set(prev)
			for (const m of marketsToPause) {
				next.add(m.marketAddress)
			}
			return next
		})

		try {
			// Validate all market addresses
			const invalidMarkets = marketsToPause.filter(m => !isAddress(m.marketAddress))
			if (invalidMarkets.length > 0) {
				throw new Error(
					`Invalid market addresses: ${invalidMarkets.map(m => m.marketAddress).join(", ")}`,
				)
			}

			// Build batch of encoded pause transactions
			const transactions = marketsToPause.map(market =>
				encodeEmergencyPause({ market: getAddress(market.marketAddress) }),
			)

			// Execute all in one transaction via Multicall3
			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })

			// Update dialog to confirming state
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			// Wait for confirmation
			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
			} else {
				setTxDialog(prev => ({
					...prev,
					status: "error",
					errorMessage: "Transaction reverted on chain",
				}))
			}

			// Mark all as successful
			setTxResults(prev => {
				const next = new Map(prev)
				for (const m of marketsToPause) {
					next.set(`pause-${m.marketAddress}`, { hash: txHash, status: "success" })
				}
				return next
			})
		} catch (error) {
			console.error("Batch pause failed:", error)
			setTxDialog(prev => ({
				...prev,
				status: "error",
				errorMessage: error instanceof Error ? error.message : "Transaction failed",
			}))
			setTxResults(prev => {
				const next = new Map(prev)
				for (const m of marketsToPause) {
					next.set(`pause-${m.marketAddress}`, { hash: "", status: "error" })
				}
				return next
			})
		} finally {
			setExecutingMarkets(prev => {
				const next = new Set(prev)
				for (const m of marketsToPause) {
					next.delete(m.marketAddress)
				}
				return next
			})
		}

		setIsPausingAll(false)
		queryClient.invalidateQueries({ queryKey: ["admin", "emergency"] })
	}

	// Handle unpause all paused markets in a single batch transaction
	const handleUnpauseAll = async () => {
		if (!walletClient || !marketStatuses) {
			return
		}

		// Pre-flight: Check wallet is authorized resolver
		if (!onChainResolver) {
			toast.error("Could not fetch resolver from registry. Please try again.")
			return
		}
		if (!connectedAddress || getAddress(connectedAddress) !== getAddress(onChainResolver)) {
			toast.error(
				`Only the resolver can unpause markets. Connected: ${truncateAddress(connectedAddress ?? "0x0", 6)}, Resolver: ${truncateAddress(onChainResolver, 6)}`,
			)
			return
		}

		// Filter paused markets that haven't been unpaused yet
		const marketsToUnpause = marketStatuses.filter(
			m => m.isPaused && !txResults.get(`unpause-${m.marketAddress}`),
		)

		if (marketsToUnpause.length === 0) {
			return
		}

		setIsUnpausingAll(true)

		// Open dialog in pending state
		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Unpause All Markets",
			description: `Unpausing ${marketsToUnpause.length} markets...`,
			errorMessage: "",
			operationCount: marketsToUnpause.length,
		})

		// Mark all markets as executing
		setExecutingMarkets(prev => {
			const next = new Set(prev)
			for (const m of marketsToUnpause) {
				next.add(m.marketAddress)
			}
			return next
		})

		try {
			// Validate all market addresses
			const invalidMarkets = marketsToUnpause.filter(m => !isAddress(m.marketAddress))
			if (invalidMarkets.length > 0) {
				throw new Error(
					`Invalid market addresses: ${invalidMarkets.map(m => m.marketAddress).join(", ")}`,
				)
			}

			// Build batch of encoded unpause transactions
			const transactions = marketsToUnpause.map(market =>
				encodeEmergencyUnpause({ market: getAddress(market.marketAddress) }),
			)

			// Execute all in one transaction via Multicall3
			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })

			// Update dialog to confirming state
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			// Wait for confirmation
			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
			} else {
				setTxDialog(prev => ({
					...prev,
					status: "error",
					errorMessage: "Transaction reverted on chain",
				}))
			}

			// Mark all as successful
			setTxResults(prev => {
				const next = new Map(prev)
				for (const m of marketsToUnpause) {
					next.set(`unpause-${m.marketAddress}`, { hash: txHash, status: "success" })
				}
				return next
			})
		} catch (error) {
			console.error("Batch unpause failed:", error)
			setTxDialog(prev => ({
				...prev,
				status: "error",
				errorMessage: error instanceof Error ? error.message : "Transaction failed",
			}))
			setTxResults(prev => {
				const next = new Map(prev)
				for (const m of marketsToUnpause) {
					next.set(`unpause-${m.marketAddress}`, { hash: "", status: "error" })
				}
				return next
			})
		} finally {
			setExecutingMarkets(prev => {
				const next = new Set(prev)
				for (const m of marketsToUnpause) {
					next.delete(m.marketAddress)
				}
				return next
			})
		}

		setIsUnpausingAll(false)
		queryClient.invalidateQueries({ queryKey: ["admin", "emergency"] })
	}

	// Toggle market selection
	const toggleMarketSelection = (marketAddress: string) => {
		setSelectedMarkets(prev => {
			const next = new Set(prev)
			if (next.has(marketAddress)) {
				next.delete(marketAddress)
			} else {
				next.add(marketAddress)
			}
			return next
		})
	}

	// Select all active markets (not paused)
	const selectAllActive = () => {
		if (marketStatuses) {
			setSelectedMarkets(new Set(marketStatuses.filter(m => !m.isPaused).map(m => m.marketAddress)))
		}
	}

	// Select all paused markets
	const selectAllPaused = () => {
		if (marketStatuses) {
			setSelectedMarkets(new Set(marketStatuses.filter(m => m.isPaused).map(m => m.marketAddress)))
		}
	}

	// Generate Safe batch for pausing selected markets
	const generatePauseBatch = () => {
		if (selectedMarkets.size === 0) {
			return null
		}

		const transactions: SafeTransaction[] = Array.from(selectedMarkets).map(marketAddress =>
			encodeEmergencyPause({ market: getAddress(marketAddress) }),
		)

		const batch = toSafeTransactionBatch({
			name: `Emergency Pause - ${selectedMarkets.size} markets`,
			transactions,
			chainId,
		})

		return serializeSafeBatch(batch)
	}

	// Generate Safe batch for unpausing selected markets
	const generateUnpauseBatch = () => {
		if (selectedMarkets.size === 0) {
			return null
		}

		const transactions: SafeTransaction[] = Array.from(selectedMarkets).map(marketAddress =>
			encodeEmergencyUnpause({ market: getAddress(marketAddress) }),
		)

		const batch = toSafeTransactionBatch({
			name: `Emergency Unpause - ${selectedMarkets.size} markets`,
			transactions,
			chainId,
		})

		return serializeSafeBatch(batch)
	}

	// Count paused/active markets
	const pausedCount = marketStatuses?.filter(m => m.isPaused).length ?? 0
	const activeCount =
		marketStatuses?.filter(m => !m.isPaused && m.statusType !== "unknown").length ?? 0

	if (!isConnected) {
		return (
			<div className="py-20 text-center">
				<p className="text-gray-400">Connect your wallet to access emergency controls.</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="mb-2 font-bold text-2xl">Emergency Controls</h1>
				<p className="text-gray-400">Pause or unpause markets in case of emergency</p>
			</div>

			{/* Warning Banner */}
			<div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
				<div className="flex items-center gap-2">
					<span aria-hidden="true" className="text-red-400 text-xl">
						&#9888;
					</span>
					<span className="font-medium text-red-400">Emergency Actions</span>
				</div>
				<p className="mt-2 text-red-300/80 text-sm">
					Pausing a market prevents all trading activity. Use only in emergencies. Ensure you have
					proper authorization before executing these actions.
				</p>
			</div>

			{/* Resolver Authorization Check */}
			{isHotWalletMode && !resolverLoading && !isResolver && (
				<div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
					<div className="flex items-center gap-2">
						<span aria-hidden="true" className="text-xl text-yellow-400">
							&#9888;
						</span>
						<span className="font-medium text-yellow-400">Not Authorized</span>
					</div>
					<p className="mt-2 text-sm text-yellow-300/80">
						Your connected wallet is not the resolver. Only the resolver can pause/unpause markets.
					</p>
					<div className="mt-2 space-y-1 font-mono text-xs">
						<p className="text-yellow-300/60">
							Connected: {connectedAddress ? truncateAddress(connectedAddress, 6) : "Not connected"}
						</p>
						<p className="text-yellow-300/60">
							Resolver: {onChainResolver ? truncateAddress(onChainResolver, 6) : "Loading..."}
						</p>
					</div>
				</div>
			)}

			{/* Mode Indicator */}
			<div className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
				<span className="text-gray-500 text-sm">Mode:</span>
				<span className={cn("font-medium", isHotWalletMode ? "text-green-400" : "text-blue-400")}>
					{isHotWalletMode ? "Hot Wallet (Direct Execution)" : "Safe Batch (Generate JSON)"}
				</span>
			</div>

			{/* Week Selector */}
			<div className="flex flex-wrap gap-2">
				{weeks?.map(week => (
					<button
						className={cn(
							"rounded-lg px-3 py-2 text-sm transition",
							selectedWeekId === week.weekId
								? "bg-gray-600 text-white"
								: "bg-gray-800 text-gray-400 hover:bg-gray-700",
						)}
						key={week.weekId}
						onClick={() => {
							setSelectedWeekId(week.weekId)
							setSelectedMarkets(new Set())
						}}
						type="button"
					>
						<span className="font-mono">{week.weekId.slice(0, 8)}</span>
						<span className="ml-2 text-xs opacity-70">({week.marketCount})</span>
					</button>
				))}
			</div>

			{/* Status Summary */}
			{selectedWeekId && marketStatuses && (
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
						<div className="mb-1 text-gray-500 text-sm">Active Markets</div>
						<div className="font-semibold text-green-400 text-xl">{activeCount}</div>
					</div>
					<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
						<div className="mb-1 text-gray-500 text-sm">Paused Markets</div>
						<div className="font-semibold text-red-400 text-xl">{pausedCount}</div>
					</div>
				</div>
			)}

			{/* Hot Wallet Batch Actions */}
			{selectedWeekId && isHotWalletMode && marketStatuses && (
				<div className="flex items-center gap-4">
					<button
						className="rounded-lg bg-red-600 px-4 py-2 font-medium text-sm text-white hover:bg-red-500 disabled:opacity-50"
						disabled={
							isPausingAll ||
							isUnpausingAll ||
							marketStatuses.filter(
								m =>
									!m.isPaused &&
									m.statusType !== "unknown" &&
									!txResults.get(`pause-${m.marketAddress}`),
							).length === 0
						}
						onClick={() => handlePauseAll().catch(err => toast.error(err.message))}
						type="button"
					>
						{isPausingAll
							? "Pausing..."
							: `Pause All Active (${marketStatuses.filter(m => !m.isPaused && m.statusType !== "unknown" && !txResults.get(`pause-${m.marketAddress}`)).length})`}
					</button>
					<button
						className="rounded-lg bg-green-600 px-4 py-2 font-medium text-sm text-white hover:bg-green-500 disabled:opacity-50"
						disabled={
							isPausingAll ||
							isUnpausingAll ||
							marketStatuses.filter(m => m.isPaused && !txResults.get(`unpause-${m.marketAddress}`))
								.length === 0
						}
						onClick={() => handleUnpauseAll().catch(err => toast.error(err.message))}
						type="button"
					>
						{isUnpausingAll
							? "Unpausing..."
							: `Unpause All (${marketStatuses.filter(m => m.isPaused && !txResults.get(`unpause-${m.marketAddress}`)).length})`}
					</button>
				</div>
			)}

			{/* Safe Batch Actions */}
			{selectedWeekId && isSafeMode && selectedMarkets.size > 0 && (
				<div className="flex items-center gap-4 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
					<span className="text-blue-400 text-sm">{selectedMarkets.size} markets selected</span>
					<button
						className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-500"
						onClick={() => {
							const json = generatePauseBatch()
							if (json) {
								navigator.clipboard.writeText(json)
								toast.success("Pause batch copied to clipboard!")
							}
						}}
						type="button"
					>
						Copy Pause Batch
					</button>
					<button
						className="rounded bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-500"
						onClick={() => {
							const json = generateUnpauseBatch()
							if (json) {
								navigator.clipboard.writeText(json)
								toast.success("Unpause batch copied to clipboard!")
							}
						}}
						type="button"
					>
						Copy Unpause Batch
					</button>
				</div>
			)}

			{/* Markets Table */}
			{selectedWeekId && (
				<div className="rounded-lg border border-gray-800 bg-gray-900">
					<div className="flex items-center justify-between border-gray-800 border-b px-4 py-3">
						<h2 className="font-medium">Markets</h2>
						{isSafeMode && (
							<div className="flex gap-2">
								<button
									className="text-green-400 text-sm hover:text-green-300"
									onClick={selectAllActive}
									type="button"
								>
									Select Active
								</button>
								<button
									className="text-red-400 text-sm hover:text-red-300"
									onClick={selectAllPaused}
									type="button"
								>
									Select Paused
								</button>
							</div>
						)}
					</div>
					<div className="overflow-x-auto">
						{statusLoading ? (
							<table className="w-full text-left text-sm">
								<thead>
									<tr className="border-gray-800 border-b text-gray-500">
										<th className="px-4 py-3">Market</th>
										<th className="px-4 py-3">Status</th>
										<th className="px-4 py-3">Paused</th>
										<th className="px-4 py-3">Actions</th>
									</tr>
								</thead>
								<tbody>
									<TableSkeleton columns={4} rows={10} />
								</tbody>
							</table>
						) : (
							<table className="w-full text-left text-sm">
								<thead>
									<tr className="border-gray-800 border-b text-gray-500">
										{isSafeMode && (
											<th className="w-10 px-4 py-3" scope="col">
												<span className="sr-only">Selection</span>
											</th>
										)}
										<th className="px-4 py-3">Market</th>
										<th className="px-4 py-3">Status</th>
										<th className="px-4 py-3">Paused</th>
										{isHotWalletMode && <th className="px-4 py-3">Actions</th>}
									</tr>
								</thead>
								<tbody>
									{marketStatuses?.map(market => {
										const pauseResult = txResults.get(`pause-${market.marketAddress}`)
										const unpauseResult = txResults.get(`unpause-${market.marketAddress}`)
										const isExecuting = executingMarkets.has(market.marketAddress)

										return (
											<tr
												className="border-gray-800 border-b hover:bg-gray-800/50"
												key={market.marketAddress}
											>
												{isSafeMode && (
													<td className="px-4 py-3">
														<input
															aria-label={`Select ${market.displayName} for batch operation`}
															checked={selectedMarkets.has(market.marketAddress)}
															className="size-4 rounded border-gray-600 bg-gray-700"
															onChange={() => toggleMarketSelection(market.marketAddress)}
															type="checkbox"
														/>
													</td>
												)}
												<td className="px-4 py-3">
													<div className="font-medium">{market.displayName}</div>
													<div className="font-mono text-gray-500 text-xs">
														{truncateAddress(market.marketAddress, 6)}
													</div>
												</td>
												<td className="px-4 py-3">
													<StatusBadge status={market.statusType} />
												</td>
												<td className="px-4 py-3">
													{market.isPaused ? (
														<span className="rounded bg-red-500/20 px-2 py-0.5 text-red-400 text-xs">
															PAUSED
														</span>
													) : (
														<span className="rounded bg-green-500/20 px-2 py-0.5 text-green-400 text-xs">
															ACTIVE
														</span>
													)}
												</td>
												{isHotWalletMode && (
													<td className="px-4 py-3">
														<div className="flex items-center gap-2">
															{market.isPaused ? (
																unpauseResult?.status === "success" ? (
																	<a
																		aria-label="View unpause transaction on block explorer (opens in new tab)"
																		className="text-green-400 text-xs"
																		href={getExplorerTxUrl(chainId, unpauseResult.hash)}
																		rel="noopener noreferrer"
																		target="_blank"
																	>
																		Unpaused
																	</a>
																) : (
																	<button
																		className="rounded bg-green-600 px-3 py-1 text-white text-xs hover:bg-green-500 disabled:opacity-50"
																		disabled={isExecuting}
																		onClick={() => handleUnpause(market.marketAddress)}
																		type="button"
																	>
																		{isExecuting ? "..." : "Unpause"}
																	</button>
																)
															) : pauseResult?.status === "success" ? (
																<a
																	aria-label="View pause transaction on block explorer (opens in new tab)"
																	className="text-red-400 text-xs"
																	href={getExplorerTxUrl(chainId, pauseResult.hash)}
																	rel="noopener noreferrer"
																	target="_blank"
																>
																	Paused
																</a>
															) : (
																<button
																	className="rounded bg-red-600 px-3 py-1 text-white text-xs hover:bg-red-500 disabled:opacity-50"
																	disabled={isExecuting}
																	onClick={() => handlePause(market.marketAddress)}
																	type="button"
																>
																	{isExecuting ? "..." : "Pause"}
																</button>
															)}
														</div>
													</td>
												)}
											</tr>
										)
									})}
								</tbody>
							</table>
						)}
					</div>
				</div>
			)}

			{!selectedWeekId && (
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-8 text-center text-gray-500">
					Select a week to view markets
				</div>
			)}

			{/* Transaction Status Dialog */}
			<TransactionStatusDialog
				chainId={chainId}
				description={txDialog.description}
				errorMessage={txDialog.errorMessage}
				onOpenChange={open => setTxDialog(prev => ({ ...prev, open }))}
				open={txDialog.open}
				operationCount={txDialog.operationCount}
				status={txDialog.status}
				title={txDialog.title}
				txHash={txDialog.hash}
			/>
		</div>
	)
}

function StatusBadge({ status }: { status: string }) {
	const colors: Record<string, string> = {
		pending: "bg-yellow-500/20 text-yellow-400",
		open: "bg-green-500/20 text-green-400",
		closed: "bg-orange-500/20 text-orange-400",
		resolved: "bg-blue-500/20 text-blue-400",
		finalized: "bg-gray-500/20 text-gray-400",
		paused: "bg-red-500/20 text-red-400",
		unknown: "bg-gray-500/20 text-gray-400",
	}

	return (
		<span className={cn("rounded px-2 py-0.5 text-xs", colors[status] ?? colors.unknown)}>
			{status.toUpperCase()}
		</span>
	)
}
